import { emptyAdfDocument, isEditableAdfMark, isEditableAdfNode, } from './adf.js';
/**
 * Двусторонний конвертер между ADF и разметкой редактора.
 *
 * Редактор работает с подмножеством: абзацы, заголовки, списки, код, цитаты, разделители и
 * начертания. Всё остальное — панели, таблицы, медиа, упоминания, статусы — не разбирается,
 * а откладывается в сторону и подставляется обратно при сохранении тем же JSON, каким пришло.
 * Поэтому правка описания не может незаметно снести таблицу из середины документа.
 *
 * Неразобранный блок обозначается в тексте меткой вида ⟦1⟧. Пользователь видит на её месте
 * плашку «не поддерживается»; удалить блок он может, убрав метку, — это осознанное действие,
 * а не побочный эффект редактирования.
 */
const PLACEHOLDER_PATTERN = /^⟦(\d+)⟧$/u;
// ── ADF → текст ──────────────────────────────────────────────────────────────
export function adfToEditor(doc) {
    if (!doc || !Array.isArray(doc.content))
        return { text: '', preserved: [] };
    const preserved = [];
    const blocks = doc.content.map((node) => blockToText(node, preserved, 0));
    return { text: blocks.join('\n\n').replace(/\n{3,}/gu, '\n\n').trim(), preserved };
}
function blockToText(node, preserved, depth) {
    if (!isEditableAdfNode(node.type))
        return keepAside(node, preserved);
    switch (node.type) {
        case 'paragraph':
            return inlineToText(node.content ?? [], preserved);
        case 'heading': {
            const level = clampLevel(node.attrs?.['level']);
            return `${'#'.repeat(level)} ${inlineToText(node.content ?? [], preserved)}`;
        }
        case 'bulletList':
        case 'orderedList':
            return listToText(node, preserved, depth);
        case 'codeBlock': {
            const language = typeof node.attrs?.['language'] === 'string' ? node.attrs['language'] : '';
            const body = (node.content ?? []).map((child) => child.text ?? '').join('');
            return `\`\`\`${language}\n${body}\n\`\`\``;
        }
        case 'blockquote':
            return (node.content ?? [])
                .map((child) => blockToText(child, preserved, depth))
                .join('\n\n')
                .split('\n')
                .map((line) => `> ${line}`.trimEnd())
                .join('\n');
        case 'rule':
            return '---';
        default:
            return keepAside(node, preserved);
    }
}
function listToText(node, preserved, depth) {
    const ordered = node.type === 'orderedList';
    const indent = '  '.repeat(depth);
    return (node.content ?? [])
        .map((item, index) => {
        const marker = ordered ? `${index + 1}. ` : '- ';
        const parts = (item.content ?? []).map((child) => child.type === 'bulletList' || child.type === 'orderedList'
            ? listToText(child, preserved, depth + 1)
            : blockToText(child, preserved, depth));
        const [first = '', ...rest] = parts;
        const head = `${indent}${marker}${first}`;
        // Вложенные списки уже несут собственный отступ, остальное выравнивается под маркер.
        const tail = rest.map((part) => part.startsWith('  ') ? part : `${indent}  ${part}`);
        return [head, ...tail].join('\n');
    })
        .join('\n');
}
/** Узел, который редактор не понимает: сохраняем целиком и оставляем в тексте метку. */
function keepAside(node, preserved) {
    preserved.push(node);
    return `⟦${preserved.length}⟧`;
}
function inlineToText(nodes, preserved) {
    return nodes
        .map((node) => {
        if (node.type === 'hardBreak')
            return '\n';
        if (node.type !== 'text') {
            // Упоминания, эмодзи, карточки ссылок текстом не выразить — откладываем их так же,
            // как блоки. Иначе правка соседнего слова превращала бы карточку в простой текст.
            return keepAside(node, preserved);
        }
        let text = node.text ?? '';
        const marks = node.marks ?? [];
        // Код применяется первым: внутри него разметка не действует.
        if (marks.some((mark) => mark.type === 'code'))
            return `\`${text}\``;
        for (const mark of marks) {
            if (!isEditableAdfMark(mark.type))
                continue;
            if (mark.type === 'strong')
                text = `**${text}**`;
            else if (mark.type === 'em')
                text = `_${text}_`;
            else if (mark.type === 'strike')
                text = `~~${text}~~`;
            else if (mark.type === 'link') {
                const href = typeof mark.attrs?.['href'] === 'string' ? mark.attrs['href'] : '';
                text = `[${text}](${href})`;
            }
        }
        return text;
    })
        .join('');
}
function clampLevel(value) {
    const level = typeof value === 'number' ? Math.trunc(value) : 1;
    return Math.min(Math.max(level, 1), 6);
}
// ── Текст → ADF ──────────────────────────────────────────────────────────────
export function editorToAdf(input) {
    const lines = input.text.replace(/\r\n/gu, '\n').split('\n');
    const content = [];
    let index = 0;
    while (index < lines.length) {
        const line = lines[index] ?? '';
        if (line.trim().length === 0) {
            index += 1;
            continue;
        }
        // Сохранённый блок возвращается ровно тем же объектом, каким пришёл из Jira.
        const placeholder = PLACEHOLDER_PATTERN.exec(line.trim());
        if (placeholder) {
            const position = Number.parseInt(placeholder[1] ?? '', 10) - 1;
            const node = input.preserved[position];
            if (node)
                content.push(node);
            index += 1;
            continue;
        }
        if (line.startsWith('```')) {
            const language = line.slice(3).trim();
            const body = [];
            index += 1;
            while (index < lines.length && !(lines[index] ?? '').startsWith('```')) {
                body.push(lines[index] ?? '');
                index += 1;
            }
            index += 1; // закрывающая ограда
            content.push({
                type: 'codeBlock',
                ...(language ? { attrs: { language } } : {}),
                content: body.length > 0 ? [{ type: 'text', text: body.join('\n') }] : [],
            });
            continue;
        }
        if (/^-{3,}$/u.test(line.trim())) {
            content.push({ type: 'rule' });
            index += 1;
            continue;
        }
        const heading = /^(#{1,6})\s+(.*)$/u.exec(line);
        if (heading) {
            content.push({
                type: 'heading',
                attrs: { level: heading[1]?.length ?? 1 },
                content: textToInline(heading[2] ?? '', input.preserved),
            });
            index += 1;
            continue;
        }
        if (line.startsWith('>')) {
            const quoted = [];
            while (index < lines.length && (lines[index] ?? '').startsWith('>')) {
                quoted.push((lines[index] ?? '').replace(/^>\s?/u, ''));
                index += 1;
            }
            content.push({
                type: 'blockquote',
                content: [{ type: 'paragraph', content: textToInline(quoted.join('\n'), input.preserved) }],
            });
            continue;
        }
        if (isListLine(line)) {
            const [list, next] = parseList(lines, index, 0, input.preserved);
            content.push(list);
            index = next;
            continue;
        }
        // Обычный абзац: собираем подряд идущие строки до пустой или до начала другого блока.
        const paragraph = [];
        while (index < lines.length) {
            const current = lines[index] ?? '';
            if (current.trim().length === 0 ||
                current.startsWith('```') ||
                current.startsWith('>') ||
                /^(#{1,6})\s+/u.test(current) ||
                /^-{3,}$/u.test(current.trim()) ||
                isListLine(current) ||
                PLACEHOLDER_PATTERN.test(current.trim())) {
                break;
            }
            paragraph.push(current);
            index += 1;
        }
        content.push({ type: 'paragraph', content: textToInline(paragraph.join('\n'), input.preserved) });
    }
    return { type: 'doc', version: 1, content };
}
function isListLine(line) {
    return /^\s*(?:[-*]\s+|\d+\.\s+)/u.test(line);
}
function indentOf(line) {
    const match = /^(\s*)/u.exec(line);
    return Math.floor((match?.[1]?.length ?? 0) / 2);
}
/** Разбор списка вместе с вложенными уровнями. Возвращает узел и позицию следующей строки. */
function parseList(lines, start, depth, preserved) {
    const first = lines[start] ?? '';
    const ordered = /^\s*\d+\.\s+/u.test(first);
    const items = [];
    let index = start;
    while (index < lines.length) {
        const line = lines[index] ?? '';
        if (!isListLine(line))
            break;
        const lineDepth = indentOf(line);
        if (lineDepth < depth)
            break;
        if (lineDepth > depth) {
            // Вложенный список прикрепляется к последнему пункту текущего уровня.
            const [nested, next] = parseList(lines, index, lineDepth, preserved);
            const last = items[items.length - 1];
            if (last)
                last.content = [...(last.content ?? []), nested];
            else
                items.push({ type: 'listItem', content: [nested] });
            index = next;
            continue;
        }
        const lineOrdered = /^\s*\d+\.\s+/u.test(line);
        if (lineOrdered !== ordered)
            break;
        const text = line.replace(/^\s*(?:[-*]\s+|\d+\.\s+)/u, '');
        items.push({
            type: 'listItem',
            content: [{ type: 'paragraph', content: textToInline(text, preserved) }],
        });
        index += 1;
    }
    return [{ type: ordered ? 'orderedList' : 'bulletList', content: items }, index];
}
/**
 * Разбор начертаний внутри строки. Код обрабатывается первым и не разбирается дальше:
 * внутри `code` разметка должна оставаться буквальной.
 */
export function textToInline(text, preserved = []) {
    if (text.length === 0)
        return [];
    const nodes = [];
    for (const chunk of text.split('\n')) {
        if (nodes.length > 0)
            nodes.push({ type: 'hardBreak' });
        nodes.push(...parseInline(chunk, [], preserved));
    }
    return nodes;
}
const INLINE_RULES = [
    { pattern: /`([^`]+)`/u, build: () => 'code' },
    {
        pattern: /\[([^\]]+)\]\(([^)\s]+)\)/u,
        build: (match) => ({ type: 'link', attrs: { href: match[2] ?? '' } }),
    },
    { pattern: /\*\*([^*]+)\*\*/u, build: () => ({ type: 'strong' }) },
    { pattern: /~~([^~]+)~~/u, build: () => ({ type: 'strike' }) },
    { pattern: /(?<![\w_])_([^_]+)_(?![\w_])/u, build: () => ({ type: 'em' }) },
];
function parseInline(text, marks, preserved) {
    if (text.length === 0)
        return [];
    // Метка отложенного узла внутри строки: возвращаем исходный узел, а не его текстовый след.
    const inlinePlaceholder = /⟦(\d+)⟧/u.exec(text);
    if (inlinePlaceholder) {
        const position = Number.parseInt(inlinePlaceholder[1] ?? '', 10) - 1;
        const node = preserved[position];
        const before = text.slice(0, inlinePlaceholder.index);
        const after = text.slice(inlinePlaceholder.index + inlinePlaceholder[0].length);
        return [
            ...parseInline(before, marks, preserved),
            ...(node ? [node] : []),
            ...parseInline(after, marks, preserved),
        ];
    }
    let earliest = null;
    for (const rule of INLINE_RULES) {
        const match = rule.pattern.exec(text);
        if (match && (earliest === null || match.index < earliest.index)) {
            earliest = { index: match.index, match, rule };
        }
    }
    if (!earliest) {
        return [{ type: 'text', text, ...(marks.length > 0 ? { marks } : {}) }];
    }
    const { match, rule } = earliest;
    const before = text.slice(0, match.index);
    const after = text.slice(match.index + match[0].length);
    const inner = match[1] ?? '';
    const produced = rule.build(match);
    const nodes = [];
    if (before.length > 0) {
        nodes.push({ type: 'text', text: before, ...(marks.length > 0 ? { marks } : {}) });
    }
    if (produced === 'code') {
        nodes.push({ type: 'text', text: inner, marks: [...marks, { type: 'code' }] });
    }
    else {
        nodes.push(...parseInline(inner, [...marks, produced], preserved));
    }
    nodes.push(...parseInline(after, marks, preserved));
    return nodes;
}
/** Пустое описание Jira принимает только как документ с пустым содержимым, не как null. */
export function editorToAdfOrEmpty(input) {
    const doc = editorToAdf(input);
    return doc.content.length > 0 ? doc : emptyAdfDocument();
}
//# sourceMappingURL=adf-text.js.map